/**
 * [HOTFIX Availability v3 §Part 3] DiamondCard - one high-conviction
 * daily deep Find. Hero card ABOVE Finds/Flags on the Dashboard.
 * Renamed 2026-07-30 per founder Addendum 2 (Fades and Finds).
 *
 * Selection rules (backend-driven, deterministic):
 *   • Overall ADP rank > 100.
 *   • Composite confidence >= 0.55.
 *   • Positive divergence (PGF > ADP).
 *   • 1.25x bonus when driving axis is opportunity or matchup.
 *   • Excludes: FA / IR / OUT / INSUFFICIENT DATA.
 *   • Deterministic tie-break by player_id.
 *
 * Paid-gated via `useEntitlements().hasSeasonAnalytics`. Marketing
 * surface - designed to be shareable.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Gem, User, ChevronRight } from "lucide-react";
import { authHeaders } from "../../contexts/AuthContext";
import { useEntitlements } from "../../hooks/useEntitlements";
import { UnlistedPill } from "../analytics/AdpDisplay";
import { PaywallOverlay } from "../analytics/PaywallOverlay";
import EdgePlayerModal from "./EdgePlayerModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DiamondCard({
  seasonYear = 2026,
  scoringFormat = "half_ppr",
  leagueId = null,
  onSelect = null,
  onUpgrade = null,
  isDark = true,
}) {
  const { hasSeasonAnalytics, hasDraftAnalytics, loading: entLoading } =
    useEntitlements();
  const isPaid = hasSeasonAnalytics || hasDraftAnalytics;
  const [diamond, setDiamond] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgErr, setImgErr] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (entLoading || !isPaid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams({
      season_year: String(seasonYear),
      scoring_format: scoringFormat,
    });
    if (leagueId) params.set("league_id", leagueId);
    axios
      .get(`${API}/analytics/edge/diamond?${params.toString()}`, {
        headers: authHeaders(),
      })
      .then((r) => {
        if (!cancelled) setDiamond(r.data?.diamond || null);
      })
      .catch(() => !cancelled && setDiamond(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [entLoading, isPaid, seasonYear, scoringFormat, leagueId]);

  const bg = isDark ? "bg-[#0F0F0F]" : "bg-white/50";
  const border = isDark ? "border-white/[0.10]" : "border-black/[0.08]";
  const text = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const sub = isDark ? "text-white/60" : "text-black/60";
  const accent = "#FF6B00";

  return (
    <section
      className={`relative rounded-2xl border ${border} ${bg} p-4 sm:p-5 mb-4 overflow-hidden`}
      data-testid="diamond-card-section"
    >
      {/* Ambient orange corner glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full"
        style={{
          background: `radial-gradient(closest-side, ${accent}22, transparent)`,
        }}
      />

      <div className="flex items-center gap-2 mb-3">
        <Gem className="w-4 h-4" style={{ color: accent }} />
        <div>
          <p
            className="font-display font-black text-[11px] uppercase tracking-widest"
            style={{ color: accent }}
          >
            Diamond in the Rough
          </p>
          <p className={`font-mono text-[10px] ${sub}`}>
            Daily deep Find past ADP #100
          </p>
        </div>
      </div>

      {/* Body */}
      {(loading || entLoading) && (
        <div className={`text-sm font-mono ${sub} py-6 text-center`} data-testid="diamond-loading">
          Loading today’s pick…
        </div>
      )}
      {!loading && !entLoading && isPaid && !diamond && (
        <div className={`text-sm font-mono ${sub} py-6 text-center`} data-testid="diamond-empty">
          No qualifying pick today. Check back tomorrow.
        </div>
      )}
      {!loading && !entLoading && isPaid && diamond && (
        <button
          type="button"
          className={`w-full text-left flex items-center gap-4 rounded-xl border ${border} p-3 sm:p-4 hover:bg-white/[0.03] transition-colors cursor-pointer`}
          data-testid="diamond-card-hero"
          onClick={() => {
            if (onSelect) onSelect(diamond);
            setModalOpen(true);
          }}
        >
          {imgErr || !diamond.photo_url ? (
            <div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center shrink-0">
              <User className="w-6 h-6 text-white/40" />
            </div>
          ) : (
            <img
              src={diamond.photo_url}
              alt={diamond.name}
              onError={() => setImgErr(true)}
              className="w-14 h-14 rounded-full object-cover bg-white/5 shrink-0"
              data-testid="diamond-card-photo"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className={`font-display font-bold text-lg ${text} truncate`}>
              {diamond.name}
            </div>
            <div className={`font-mono text-[11px] ${sub} mt-0.5`}>
              {diamond.position} · {diamond.nfl_team || "FA"}
            </div>
            <div className={`font-mono text-[12px] mt-1 ${text}`}>
              ADP:{" "}
              {diamond.adp_rank_overall != null ? (
                <span style={{ color: accent }}>#{diamond.adp_rank_overall}</span>
              ) : (
                <UnlistedPill />
              )}{" "}
              <span className="opacity-60">→</span>{" "}
              PGF:{" "}
              <span style={{ color: accent }}>#{diamond.pgf_rank_overall}</span>
            </div>
            <div className={`text-[10px] mt-1.5 uppercase tracking-widest ${sub}`} data-testid="diamond-driver">
              {diamond.driving_axis ? (
                <>
                  Edge driver:{" "}
                  <span className="lowercase" style={{ color: accent }}>{diamond.driving_axis}</span>
                  {typeof diamond.edge_pct === "number" && (
                    <>
                      {" "}<span className="opacity-60">|</span>{" "}
                      <span className="lowercase" style={{ color: accent }}>
                        edge {(diamond.edge_pct * 100).toFixed(0)}%
                      </span>
                    </>
                  )}
                </>
              ) : (
                <span className="opacity-60">Edge driver: n/a</span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end shrink-0">
            <span
              className="font-display font-bold text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full"
              style={{
                background: `${accent}22`,
                color: accent,
                border: `1px solid ${accent}55`,
              }}
              data-testid="diamond-badge"
            >
              UNDERVALUED
            </span>
            <ChevronRight className={`w-4 h-4 mt-2 ${sub}`} />
          </div>
        </button>
      )}

      {!isPaid && !entLoading && (
        <PaywallOverlay onUpgrade={onUpgrade} mode="season" leagueId={leagueId} />
      )}
      {modalOpen && diamond && (
        <EdgePlayerModal
          edge={diamond}
          onClose={() => setModalOpen(false)}
          leagueId={leagueId}
          seasonYear={seasonYear}
          scoringFormat={scoringFormat}
          isDark={isDark}
        />
      )}
    </section>
  );
}
